import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardContent, Button, CircularProgress, Chip } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import { Alert } from '@material-ui/lab'
import {
    getNppesIngestStatus,
    triggerNppesIngest,
    uploadNppesIngest,
} from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'

const statTile = { padding: 12, minWidth: 140, textAlign: 'center' }
const statValue = { fontSize: 28, fontWeight: 600, color: '#222' }
const statLabel = { fontSize: 12, color: '#666', marginTop: 4 }

const statusChipStyle = (status) => {
    switch (status) {
        case 'SUCCESS':
            return { background: 'var(--badge-approved-bg)', color: 'var(--status-approved)' }
        case 'RUNNING':
            return { background: 'var(--badge-review-bg)', color: 'var(--rox-lilac-ink)' }
        case 'FAILED':
            return { background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }
        case 'SKIPPED_NOT_MODIFIED':
            return { background: '#f3e5f5', color: '#6a1b9a' }
        default:
            return { background: 'var(--bg-canvas)', color: '#555' }
    }
}

const formatTs = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
}

const NppesUsage = () => {
    const [ingest, setIngest] = useState(null)
    const [loading, setLoading] = useState(false)
    const [triggering, setTriggering] = useState(false)
    const fileInputRef = useRef(null)

    const fetch = useCallback(() => {
        setLoading(true)
        getNppesIngestStatus()()
            .then((ingestStatus) => {
                setIngest(ingestStatus)
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to load NPI Registry status' }))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => { fetch() }, [fetch])

    // Auto-refresh every 15s while a run is in flight so the elapsed timer + counts update live.
    useEffect(() => {
        if (ingest?.lastRun?.status !== 'RUNNING') return undefined
        const interval = setInterval(fetch, 15000)
        return () => clearInterval(interval)
    }, [ingest?.lastRun?.status, fetch])

    const onFileSelected = (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        event.target.value = ''
        setTriggering(true)
        uploadNppesIngest({ file })()
            .then(() => {
                Toast({ type: 'success', message: `Ingest started from ${file.name}` })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.status === 409 ? 'An ingest is already running' : 'Failed to upload file'
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setTriggering(false))
    }

    const runIngest = (force = false) => {
        setTriggering(true)
        triggerNppesIngest({ force })()
            .then(() => {
                Toast({
                    type: 'success',
                    message: force
                        ? 'NPI Registry ingest started (force re-download)'
                        : 'NPI Registry ingest started',
                })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.status === 409
                    ? 'An ingest is already running'
                    : 'Failed to trigger ingest'
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setTriggering(false))
    }

    const lastRun = ingest?.lastRun
    const isRunning = lastRun?.status === 'RUNNING'

    return (
        <>
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h4 className='mb-0'>NPI Registry (NPPES) Usage</h4>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetch}
                    disabled={loading}
                    variant='outlined'
                    size='small'
                >
                    Refresh
                </Button>
            </div>

            <Alert severity='info' className='mb-3'>
                CMS publishes the monthly full-file (~1.1&nbsp;GB, ~8.5M providers) on or around the
                second Monday of each month. The scheduler checks daily at 03:30&nbsp;UTC and only
                downloads when the source&rsquo;s <code>Last-Modified</code> header advances — no-change
                days record <strong>SKIPPED_NOT_MODIFIED</strong> in ~1 second.
            </Alert>

            {loading && !ingest && (
                <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress /></div>
            )}

            {ingest && (
                <Card variant='outlined' className='mb-3'>
                    <CardContent>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                            <h6 className='mb-0'>Local mirror &amp; ingest</h6>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => runIngest(false)}
                                    disabled={triggering || isRunning}
                                    variant='outlined'
                                    size='small'
                                    color='primary'
                                >
                                    {isRunning ? 'Running…' : 'Check + ingest'}
                                </Button>
                                <Button
                                    startIcon={<CloudDownloadIcon />}
                                    onClick={() => runIngest(true)}
                                    disabled={triggering || isRunning}
                                    variant='outlined'
                                    size='small'
                                >
                                    Force re-download
                                </Button>
                                <Button
                                    startIcon={<CloudUploadIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={triggering || isRunning}
                                    variant='outlined'
                                    size='small'
                                    title='Ingest from a local CSV without pulling the 1.1 GB source'
                                >
                                    Upload CSV
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    accept='.csv,text/csv'
                                    onChange={onFileSelected}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                            <div style={statTile}>
                                <div style={statValue}>{ingest.totalRecords?.toLocaleString?.() ?? ingest.totalRecords ?? 0}</div>
                                <div style={statLabel}>Total providers</div>
                            </div>
                            <div style={statTile}>
                                <div style={statValue}>{ingest.activeRecords?.toLocaleString?.() ?? '—'}</div>
                                <div style={statLabel}>Active (status&nbsp;A)</div>
                            </div>
                            <div style={statTile}>
                                <div style={statValue}>{ingest.deactivatedRecords?.toLocaleString?.() ?? '—'}</div>
                                <div style={statLabel}>Deactivated (status&nbsp;D)</div>
                            </div>
                        </div>

                        {!lastRun && (
                            <div style={{ fontSize: 13, color: '#666' }}>No ingest run recorded yet.</div>
                        )}

                        {lastRun && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                    <strong style={{ fontSize: 13 }}>Last run:</strong>
                                    <Chip
                                        label={lastRun.status}
                                        size='small'
                                        style={statusChipStyle(lastRun.status)}
                                    />
                                    {isRunning && <CircularProgress size={16} />}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: 13 }}>
                                    <strong>Started:</strong><span>{formatTs(lastRun.startedAt)}</span>
                                    <strong>Finished:</strong><span>{formatTs(lastRun.finishedAt)}</span>
                                    <strong>Elapsed:</strong><span>{lastRun.elapsed ?? '—'}</span>
                                    <strong>Records upserted:</strong><span>{lastRun.recordsUpserted?.toLocaleString?.() ?? lastRun.recordsUpserted ?? 0}</span>
                                    <strong>Records removed:</strong><span>{lastRun.recordsRemoved?.toLocaleString?.() ?? lastRun.recordsRemoved ?? 0}</span>
                                    {lastRun.rowsRead != null && (
                                        <>
                                            <strong>Rows read:</strong>
                                            <span>{lastRun.rowsRead?.toLocaleString?.() ?? lastRun.rowsRead}</span>
                                        </>
                                    )}
                                    {lastRun.rowsDedupSkipped != null && (
                                        <>
                                            <strong>Rows skipped (dedup):</strong>
                                            <span>{lastRun.rowsDedupSkipped?.toLocaleString?.() ?? lastRun.rowsDedupSkipped}</span>
                                        </>
                                    )}
                                    {lastRun.rowsMalformedSkipped != null && (
                                        <>
                                            <strong>Rows skipped (malformed):</strong>
                                            <span>{lastRun.rowsMalformedSkipped?.toLocaleString?.() ?? lastRun.rowsMalformedSkipped}</span>
                                        </>
                                    )}
                                    {lastRun.sourceFilename && (
                                        <>
                                            <strong>Source file:</strong>
                                            <span style={{ wordBreak: 'break-word' }}>{lastRun.sourceFilename}</span>
                                        </>
                                    )}
                                    {lastRun.sourceLastModified && (
                                        <>
                                            <strong>Source Last-Modified:</strong>
                                            <span>{lastRun.sourceLastModified}</span>
                                        </>
                                    )}
                                    {lastRun.errorMessage && (
                                        <>
                                            <strong style={{ color: 'var(--status-declined)' }}>Error:</strong>
                                            <span style={{ color: 'var(--status-declined)', wordBreak: 'break-word' }}>{lastRun.errorMessage}</span>
                                        </>
                                    )}
                                </div>

                                {isRunning && (
                                    <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                                        Auto-refreshing every 15 seconds while the run is in flight.
                                        Full monthly ingest typically takes 30–90 minutes.
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {ingest && (
                <Card variant='outlined'>
                    <CardContent>
                        <h6 className='mb-2'>Reference</h6>
                        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                            <div><strong>Source:</strong> <a href='https://download.cms.gov/nppes/NPI_Files.html' target='_blank' rel='noopener noreferrer'>download.cms.gov/nppes/NPI_Files.html</a></div>
                            <div><strong>Cost:</strong> free — no API key required, no daily quota</div>
                            <div><strong>Scheduler:</strong> daily 03:30&nbsp;UTC (<code>roxwrite.scheduler.nppes-ingest.cron</code>)</div>
                            <div><strong>Cadence:</strong> CMS publishes monthly (~second Monday); daily check is essentially free thanks to Last-Modified short-circuit</div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </>
    )
}

export default NppesUsage
